import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * @param {string} value
 */
const upper = (value) => `${value}`.toUpperCase();

/**
 * @param {string[]} a
 * @param {string[]} b
 */
const sameArray = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * @template T
 * Reads and writes AdvancedFilter selections from URL query params.
 *
 * - Query key: filter.label
 * - Query values: repeated params (?LABEL=OPT1&LABEL=OPT2)
 * - Values are normalized to UPPERCASE
 * - Unknown values are removed while reading
 * - If a key is missing, it is seeded with an empty value
 *
 * @param {import("@seabrookstudios/pitch2table-core").AdvancedFilter<T>[]} advancedFilters
 * @returns {{
 *  selectionsByLabel: Record<string, string[]>,
 *  setSelectionsByLabel: (updater: import("react").SetStateAction<Record<string, string[]>>) => void
 * }}
 */
export const useAdvancedFilters = (advancedFilters) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const optionSetsByLabel = useMemo(() => {
    /** @type {Record<string, Set<string>>} */
    const map = {};
    for (const filter of advancedFilters) {
      map[filter.label] = new Set(filter.options.map(upper));
    }
    return map;
  }, [advancedFilters]);

  const selectionsByLabel = useMemo(() => {
    /** @type {Record<string, string[]>} */
    const record = {};
    for (const filter of advancedFilters) {
      const allowed = optionSetsByLabel[filter.label] || new Set();
      const values = searchParams
        .getAll(filter.label)
        .map(upper)
        .filter((v) => v.length > 0)
        .filter((v) => allowed.has(v));

      record[filter.label] = Array.from(new Set(values));
    }
    return record;
  }, [advancedFilters, optionSetsByLabel, searchParams.toString()]);

  // Seed missing params and clean invalid/non-uppercased ones without pushing history.
  useEffect(() => {
    if (advancedFilters.length === 0) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    let changed = false;

    for (const filter of advancedFilters) {
      const allowed = optionSetsByLabel[filter.label] || new Set();
      const rawValues = nextParams.getAll(filter.label);

      // Seed missing key
      if (rawValues.length === 0 && !nextParams.has(filter.label)) {
        nextParams.set(filter.label, "");
        changed = true;
        continue;
      }

      const cleaned = rawValues
        .map(upper)
        .filter((v) => v.length > 0)
        .filter((v) => allowed.has(v));

      const cleanedUnique = Array.from(new Set(cleaned));

      // Normalize URL to cleaned (and uppercase) values.
      const currentUpperUnique = Array.from(new Set(rawValues.map(upper).filter((v) => v.length > 0)));
      const needsUpdate =
        !sameArray(cleanedUnique, currentUpperUnique) || !sameArray(cleanedUnique, rawValues.filter(Boolean));

      if (needsUpdate) {
        nextParams.delete(filter.label);
        if (cleanedUnique.length === 0) {
          nextParams.set(filter.label, "");
        } else {
          cleanedUnique.forEach((v) => nextParams.append(filter.label, v));
        }
        changed = true;
      }
    }

    if (changed) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [advancedFilters, optionSetsByLabel, searchParams, setSearchParams]);

  const setSelectionsByLabel = useCallback(
    (updater) => {
      const prev = selectionsByLabel;
      const next = typeof updater === "function" ? updater(prev) : updater;

      const nextParams = new URLSearchParams(searchParams);

      for (const filter of advancedFilters) {
        const allowed = optionSetsByLabel[filter.label] || new Set();
        const desired = (next[filter.label] || [])
          .map(upper)
          .filter((v) => v.length > 0)
          .filter((v) => allowed.has(v));

        const uniqueDesired = Array.from(new Set(desired));

        nextParams.delete(filter.label);
        if (uniqueDesired.length === 0) {
          nextParams.set(filter.label, "");
        } else {
          uniqueDesired.forEach((v) => nextParams.append(filter.label, v));
        }
      }

      setSearchParams(nextParams);
    },
    [advancedFilters, optionSetsByLabel, searchParams, selectionsByLabel, setSearchParams]
  );

  return { selectionsByLabel, setSelectionsByLabel };
};
